import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';

import CustomAutocomplete from 'src/components/custom-autocomplete/custom-autocomplete';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';

import { useBenefitFilter } from './benefit-filter.hook';

export interface BenefitFilterProps {
  getData: () => void;
  handleAddNew: () => void;
}

export default function BenefitFilter({ getData, handleAddNew }: BenefitFilterProps) {
  const {
    categoriesOptions,
    levelsOptions,
    typeOptions,
    statusOptions,
    selectedCategories,
    selectedLevels,
    selectedTypes,
    selectedStatus,
    handleRemoveAllFilters,
    handleRemoveCategoryFilter,
    handleRemoveLevelFilter,
    handleRemoveTypeFilter,
    handleSelectCategory,
    handleSelectLevel,
    handleSelectType,
    handleSelectStatus,
    onSearch,
    searchValue,
  } = useBenefitFilter(getData);

  const responsive = useResponsive;
  const isMd = responsive('down', 'lg');

  return (
    <div data-testid="benefit-filter-render-test">
      <Stack
        alignItems="center"
        direction={isMd ? 'column' : 'row'}
        spacing={2}
        width="100%"
        sx={{ overflow: 'visible', minHeight: '48px' }}
      >
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          width={isMd ? '100%' : 'auto'}
          justifyContent="flex-start"
          alignItems="center"
          sx={{
            overflow: 'visible',
            flex: 1,
            minWidth: 0,
          }}
        >
          <TextField
            placeholder="Busque pelo Benefício Desejado"
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            InputProps={{
              sx: { borderRadius: 100},
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 1 }}>
                  <Iconify icon="ri:search-line" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              style: { padding: '8px 16px' },
            }}
            sx={{
              width: 'auto',
              '& .MuiInputBase-root': {
                width: '493px',
              },
            }}
          />
          <CustomAutocomplete
            handleSelectOption={handleSelectStatus}
            options={statusOptions}
            placeholder="Status"
            disableCloseOnSelect
            sx={{
              '.MuiOutlinedInput-root': { p: 0 },
              width: 'auto',
              minWidth: 'auto',
              '& .MuiInputBase-root': {
                width: '150px',
              },
            }}
          />
          <CustomAutocomplete
            multiple
            handleSelectOption={handleSelectType}
            options={typeOptions}
            placeholder="Tipo"
            selectedOptions={selectedTypes}
            disableTags
            disableClearable
            disableCloseOnSelect
            sx={{
              '.MuiOutlinedInput-root': { p: 0 },
              width: 'auto',
              minWidth: 'auto',
              '& .MuiInputBase-root': {
                width: '150px',
              },
            }}
          />
          <CustomAutocomplete
            multiple
            handleSelectOption={handleSelectCategory}
            options={categoriesOptions}
            placeholder="Categoria"
            selectedOptions={selectedCategories}
            disableTags
            disableClearable
            disableCloseOnSelect
            sx={{
              '.MuiOutlinedInput-root': { p: 0 },
              width: 'auto',
              minWidth: 'auto',
              '& .MuiInputBase-root': {
                width: '150px',
              },
            }}
          />
          <CustomAutocomplete
            multiple
            handleSelectOption={handleSelectLevel}
            options={levelsOptions}
            placeholder="Nível"
            selectedOptions={selectedLevels}
            disableTags
            disableClearable
            disableCloseOnSelect
            sx={{
              '.MuiOutlinedInput-root': { p: 0 },
              width: 'auto',
              minWidth: 'auto',
              '& .MuiInputBase-root': {
                width: '150px',
              },
            }}
          />
        </Stack>
        <Stack alignSelf="flex-start">
          <Button variant="text" color="primary" onClick={handleAddNew}>
            <Typography typography="bold-bd-md">Adicionar Novo</Typography>
          </Button>
        </Stack>
      </Stack>
      <Box height={16} />
      {(selectedCategories?.length > 0 || selectedLevels?.length > 0 || selectedTypes?.length > 0 || selectedStatus) && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          key="filter-chips-container"
          sx={{
            overflow: 'visible',
            '& > *': {
              marginBottom: '4px !important',
            },
          }}
        >
          {selectedCategories?.map((cat) => (
            <Chip
              key={`category-${cat.value}`}
              label={cat.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleRemoveCategoryFilter(cat.value)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          ))}
          {selectedLevels?.map((level) => (
            <Chip
              key={`level-${level.value}`}
              label={level.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleRemoveLevelFilter(level.value)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          ))}
          {selectedTypes?.map((type) => (
            <Chip
              key={`type-${type.value}`}
              label={type.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleRemoveTypeFilter(type.value)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          ))}
          {selectedStatus && (
            <Chip
              key={`status-${selectedStatus.value}`}
              label={selectedStatus.label}
              color="primary"
              variant="outlined"
              sx={{ borderRadius: 100 }}
              onDelete={() => handleSelectStatus(undefined)}
              deleteIcon={<Iconify icon="ri:close-line" width={16} />}
            />
          )}
          <Button
            key="clear-filters-btn"
            onClick={handleRemoveAllFilters}
            variant="text"
            sx={{
              typography: 'body-sm',
              textDecoration: 'underline',
              ':hover': {
                bgcolor: 'transparent',
                color: 'brand.200',
                textDecoration: 'underline',
              },
            }}
            disableRipple
            color="primary"
          >
            Limpar Filtros
          </Button>
        </Stack>
      )}
    </div>
  );
}
